const toPositiveInteger = (value) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

// Episode numbers may legitimately be 0 (specials, prologues), so only negative
// and non-numeric values are rejected here.
const toEpisodeNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
};

const getEpisodeKey = (season, episode) => {
    const normalizedSeason = toPositiveInteger(season);
    const normalizedEpisode = toEpisodeNumber(episode);
    return normalizedSeason && normalizedEpisode !== null ? `${normalizedSeason}:${normalizedEpisode}` : null;
};

const cloneAnimixSeries = (series, seasonFallback) => ({
    ...series,
    season: toPositiveInteger(series?.season) || toPositiveInteger(seasonFallback) || 1,
    seriesNum: toEpisodeNumber(series?.seriesNum) ?? 1,
    source: 'animix',
    kodikOnly: false,
});

export const mergeEpisodeSources = (animixSeriesData, kodikPayload, animeId) => {
    const seasons = {};
    const episodeIndex = new Map();

    Object.entries(animixSeriesData || {}).forEach(([seasonKey, entries]) => {
        if (!Array.isArray(entries)) return;
        entries.forEach((entry) => {
            if (!entry) return;
            const normalized = cloneAnimixSeries(entry, seasonKey);
            const key = getEpisodeKey(normalized.season, normalized.seriesNum);
            if (!key || episodeIndex.has(key)) return;
            if (!seasons[normalized.season]) seasons[normalized.season] = [];
            seasons[normalized.season].push(normalized);
            episodeIndex.set(key, normalized);
        });
    });

    const translation = kodikPayload?.translation || null;
    const kodikId = kodikPayload?.kodikId || null;
    const seasonMetadata = new Map(
        (Array.isArray(kodikPayload?.seasons) ? kodikPayload.seasons : [])
            .map((season) => [toPositiveInteger(season?.season), season])
            .filter(([season]) => season)
    );

    (Array.isArray(kodikPayload?.items) ? kodikPayload.items : []).forEach((episode) => {
        const season = toPositiveInteger(episode?.season);
        const episodeNumber = toEpisodeNumber(episode?.episode);
        const key = getEpisodeKey(season, episodeNumber);
        if (!key || !episode?.playerUrl) return;
        const metadata = seasonMetadata.get(season);
        const episodeKodikId = episode?.kodikId || metadata?.kodikId || kodikId;
        const episodeTranslation = episode?.translation || metadata?.translation || translation;

        const animixEntry = episodeIndex.get(key);
        if (animixEntry) {
            Object.assign(animixEntry, {
                hasKodik: true,
                kodikId: episodeKodikId,
                kodikPlayerUrl: episode.playerUrl,
                kodikPreviewUrl: episode.previewUrl || null,
                kodikTranslation: episodeTranslation,
                kodikSeasonTitle: metadata?.title || null,
                kodikSourceSeason: toPositiveInteger(metadata?.sourceSeason),
            });
            return;
        }

        const kodikEntry = {
            id: `kodik-${animeId}-${season}-${episodeNumber}`,
            animeId: Number(animeId),
            season,
            seriesNum: episodeNumber,
            name: episode.title || `Серия ${episodeNumber}`,
            source: 'kodik',
            kodikOnly: true,
            hasKodik: true,
            kodikId: episodeKodikId,
            kodikPlayerUrl: episode.playerUrl,
            kodikPreviewUrl: episode.previewUrl || null,
            kodikTranslation: episodeTranslation,
            kodikSeasonTitle: metadata?.title || null,
            kodikSourceSeason: toPositiveInteger(metadata?.sourceSeason),
        };
        if (!seasons[season]) seasons[season] = [];
        seasons[season].push(kodikEntry);
        episodeIndex.set(key, kodikEntry);
    });

    if (kodikPayload?.moviePlayer?.playerUrl) {
        const firstAnimixEntry = Object.values(seasons).flat()[0];
        if (firstAnimixEntry) {
            Object.assign(firstAnimixEntry, {
                hasKodik: true,
                kodikId: kodikPayload.moviePlayer.kodikId || kodikId,
                kodikPlayerUrl: kodikPayload.moviePlayer.playerUrl,
                kodikPreviewUrl: kodikPayload.moviePlayer.previewUrl || null,
                kodikTranslation: kodikPayload.moviePlayer.translation || translation,
                kodikMovie: true,
            });
        }
    }

    return Object.fromEntries(
        Object.entries(seasons)
            .sort(([left], [right]) => Number(left) - Number(right))
            .map(([season, entries]) => [
                season,
                entries.sort((left, right) => Number(left.seriesNum) - Number(right.seriesNum)),
            ])
    );
};

export const flattenEpisodeSources = (seriesData) => Object.values(seriesData || {})
    .flat()
    .sort((left, right) => (
        Number(left.season) - Number(right.season)
        || Number(left.seriesNum) - Number(right.seriesNum)
    ));

export const findEpisodeSourceById = (seriesData, seriesId) => flattenEpisodeSources(seriesData)
    .find((series) => String(series.id) === String(seriesId)) || null;
